import Component from 'core/Component';
import { Event } from 'services/EventEmitter';
import { deepMerge } from 'toolbox/deepMerge';

/**
 * Class which handles SFCC Data Collection tracking. It listens to the two main events
 * dw.ac.capture(Object) used to collect analytics information for products
 * aw.ac.applyContext(Object) used to change the analytics context
 * @class
 * @classdesc It manages SFCC Data Collection events
 * @extends Component
 */
export default class ActiveData extends Component {
    /**
     * @constructor
     * @param {HTMLElement} element - ActiveData HTML container
     * @param {object} options - Options of the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            activeDataContext: null,
        }, options));
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        // All the analytics tracking action should call this event.
        Event.on('analytics.event', this.onEvent, this);
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        this.setPageContext();
    }

    /**
     * Set analytics cotext for data collection.
     * Each time the dw.ac.applyContext function is called,
     * it affects all subsequent calls to dw.ac.capture until the next call to dw.ac.applyContext.
     */
    setPageContext() {
        // Collect SFCC analytics information for product (alternative of <isobject> tag)
        if (this.options.activeDataContext) {
            window.dw.ac.applyContext(this.options.activeDataContext);
        }
    }

    /**
     * Handler called when sending basic events
     * @param { String } eventName - of the event
     * @param { Object } data - Data that needs to be passed in the event
     */
    onEvent(eventName, data = {}) {
        if (typeof eventName === 'object') {
            // if first argument is an object then we don't pass the eventName and use the default
            // The first param become the data object
            data = eventName;
        }

        if (data.eventType !== 'productImpression' || !data.sfb2ccAnalyticsData || !data.sfb2ccAnalyticsData.pid) {
            return;
        }

        const {
            impressionType, pid, sku, recommenderName, placement, sfb2cc,
        } = data.sfb2ccAnalyticsData;

        this.captureActiveDataImpression(sku, pid, impressionType);

        this.handleViewSearchActivity({
            pid, sku, placement, impressionType, sfb2cc,
        });

        if (recommenderName) {
            this.captureEinsteinImpression(pid, sku, recommenderName);
        }
    }

    /**
     * Returns some default CQuotient params
     * @param {Object} cq window.CQuotient object
     * @return {Object} CQuotient params data
     */
    getDefaultCQuotientParams(cq) {
        return {
            instanceType: cq.instanceType,
            realm: cq.realm,
            siteId: cq.siteId,
            cookieId: cq.getCQCookieId(),
            userId: cq.getCQUserId(),
            accumulate: true,
        };
    }

    /**
     * simulates sfb2cc viewSearch activity
     * @private
     * @param {Object} data object
     *  @property {String} pid - product id
     *  @property {String} sku - if the product is a variant, id of the variant
     *  @property {String} placement - the placement of the tile, to be checked against known type for Search page
     *  @property {String} impressionType - one of ['recommendation', 'searchhit', 'none']
     *  @property {Object} sfb2cc - object with search related properties
     *   @property {String} searchText - searched text
     *   @property {Boolean} personalized - if the search is personalized or not
     *   @property {String} sortingRule - ID of the effective sorting rule used
     *   @property {Array} refinements - Array with refinements applied
     *   @property {String} queryLocale - the query locale
     */
    handleViewSearchActivity(data) {
        const {
            pid, sku, placement, impressionType, sfb2cc,
        } = data;

        // early return if CQuotient is not defined or if the user is not on a search page
        if (!window.CQuotient || placement !== 'list-result-search') {
            return;
        }

        // current retrofit implementation is only for tiles.
        // detail and setproduct are still using isobject in isml through their container templates
        if (!!pid && !!impressionType && impressionType === 'searchhit') {
            const cq = window.CQuotient;
            const params = this.getDefaultCQuotientParams(cq);

            params.emailId = cq.getCQHashedEmail();
            params.loginId = cq.getCQHashedLogin();
            params.locale = cq.locale;

            const {
                searchText, personalized, sortingRule, refinements, queryLocale,
            } = sfb2cc;

            params.queryLocale = queryLocale;

            params.products = [{ id: pid, sku }];
            params.showProducts = 'true';
            params.personalized = personalized;
            params.searchText = searchText;
            params.sortingRule = sortingRule;
            params.refinements = JSON.stringify(refinements);

            // this is not ideal but that's what sfb2cc injects
            try {
                if (cq.sendActivity) {
                    cq.sendActivity(cq.clientId, 'viewSearch', params);
                } else {
                    cq.activities.push({ activityType: 'viewSearch', parameters: params });
                }
            } catch (e) {

            }
        }
    }

    /**
     * simulates sfb2cc active data capture for product impression
     * @private
     * @param { String } sku - product sku
     * @param { String } pid - product id
     * @param { String } impressionType - one of ['recommendation', 'searchhit', 'none']
     */
    captureActiveDataImpression(sku, pid, impressionType) {
        // current retrofit implementation is only for tiles.
        // detail and setproduct are still using isobject in isml through their container templates
        const allowedImpressionTypes = ['recommendation', 'searchhit', 'none'];
        const items = [];
        // we track both sku and pid to initialise master products active data
        if (sku) {
            items.push({ id: sku, type: impressionType });
        }
        if (pid) {
            items.push({ id: pid, type: impressionType });
        }
        // a productId + impression type used by a tile should trigger dw.ac.capture for active data
        if (!!items.length && !!impressionType && allowedImpressionTypes.indexOf(impressionType) !== -1) {
            window.dw.ac.capture(items);
        }
    }

    /**
     * simulates sfb2cc einstein data capture for recommendation impression
     * @private
     * @param { String } pid - product id of the master product
     * @param { String } sku - if the product is a variant, id of the variant
     * @param { String } recommenderName - the name of the recommender used to get the recommendation
     */
    captureEinsteinImpression(pid, sku, recommenderName) {
        if (!pid || !window.CQuotient) {
            return;
        }
        // if the type is 'recommendation' and the context is einstein
        // we'll need to emulate the correct call to CQuotient viewReco
        const cq = window.CQuotient;
        const params = this.getDefaultCQuotientParams(cq);
        params.products = [{
            id: pid, sku, type: '', alt_id: '',
        }];
        params.recommenderName = recommenderName;

        // this is not ideal but that's what sfb2cc injects
        try {
            if (cq.sendActivity) {
                cq.sendActivity(cq.clientId, 'viewReco', params);
            } else {
                cq.activities.push({ activityType: 'viewReco', parameters: params });
            }
        } catch (e) {

        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        Event.removeListener('analytics.event', this.onEvent, this);
    }
}
